import type { ErrorInfo, ReactNode } from 'react';
import React from 'react';
import './index.css';

interface SCErrorBoundaryProps {
  children?: ReactNode;
  /** Rendered instead of the default message when the subtree throws. */
  Fallback?: ReactNode;
  /** Namespace for QA test ids. The fallback renders as `<DataTestId>.error`. */
  DataTestId?: string;
  /** Called with the error so the caller can report it. */
  OnError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface SCErrorBoundaryState {
  hasError: boolean;
}

/**
 * Stops a render error in one subtree from unmounting everything above it.
 *
 * This matters most for SCModal: every modal renders into the single shared SCComponentRoot, and
 * an unmount there permanently resets `SCComponentRoot.renderComponent` to a no-op, so one crashing
 * modal silently blanks every modal opened afterwards (SC-830, and SC-1360 before it).
 */
class SCErrorBoundary extends React.Component<SCErrorBoundaryProps, SCErrorBoundaryState> {
  constructor(props: SCErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): SCErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('SCErrorBoundary caught an error while rendering', error, errorInfo);
    this.props.OnError?.(error, errorInfo);
  }

  render() {
    const { children, Fallback, DataTestId } = this.props;

    if (!this.state.hasError) {
      return children;
    }

    if (Fallback !== undefined) {
      return Fallback;
    }

    return (
      <div
        className="sce-error-boundary-fallback"
        data-testid={DataTestId ? `${DataTestId}.error` : undefined}
      >
        Something went wrong displaying this content.
      </div>
    );
  }
}

export { SCErrorBoundary };
export type { SCErrorBoundaryProps };
